import { useEffect, useRef, useState } from "react";
import { useThemeProvider } from "../../../../utils/ThemeContext";

import {
  CategoryScale,
  Chart,
  Filler,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  TimeScale,
  Tooltip,
} from "chart.js";
import "chartjs-adapter-moment";
import { chartColors } from "./ChartjsConfig";

// Import utilities

Chart.register(
  LineController,
  LineElement,
  Filler,
  PointElement,
  LinearScale,
  TimeScale,
  CategoryScale,
  Tooltip
);

function LineChartAnswers({ data, width, height, useTimeScale = true }) {
  const [chart, setChart] = useState(null);
  const canvas = useRef(null);
  const { currentTheme } = useThemeProvider();
  const darkMode = currentTheme === "dark";
  const {
    tooltipTitleColor,
    tooltipBodyColor,
    tooltipBgColor,
    tooltipBorderColor,
  } = chartColors;

  const labels = ["Correct Answers", "Incorrect Answers"];

  useEffect(() => {
    const ctx = canvas.current;
    const newChart = new Chart(ctx, {
      type: "line",
      data: data,
      options: {
        layout: {
          padding: 20,
        },
        scales: {
          y: {
            display: false,
          },
          x: useTimeScale
            ? {
                type: "time",
                time: {
                  parser: "MM-DD-YYYY",
                  unit: "month",
                },
                display: false,
              }
            : {
                type: "category",
                display: false,
              },
        },
        plugins: {
          tooltip: {
            titleFont: {
              weight: 600,
              size: 14,
            },
            bodyFont: {
              size: 13,
            },
            footerFont: {
              weight: 500,
              style: "italic",
              size: 12,
            },
            padding: 12,
            cornerRadius: 6,
            caretSize: 6,
            boxPadding: 4,
            displayColors: true,
            callbacks: {
              title: (context) => {
                const index = context[0].dataIndex;

                const label = data.labels[index];

                if (label && label.length > 30) {
                  const middleIndex = Math.floor(label.length / 2);

                  let splitIndex = label.indexOf(" ", middleIndex);
                  if (splitIndex === -1) {
                    splitIndex = label.lastIndexOf(" ", middleIndex);
                  }
                  if (splitIndex === -1) {
                    splitIndex = middleIndex;
                  }

                  const firstLine = label.substring(0, splitIndex);
                  const secondLine = label.substring(splitIndex + 1);
                  return [firstLine, secondLine];
                }

                return label;
              },
              label: (context) => {
                const label = labels[context.datasetIndex];
                const index = context.dataIndex;

                const correctValue = data.datasets[0].data[index] || 0;
                const incorrectValue = data.datasets[1].data[index] || 0;
                const total = correctValue + incorrectValue;

                let percentage = 0;
                if (total > 0) {
                  percentage = Math.round((context.parsed.y / total) * 100);
                }

                return `${label}: ${context.parsed.y} (${percentage}%)`;
              },
              footer: (context) => {
                const index = context[0].dataIndex;

                const correctValue = data.datasets[0].data[index] || 0;
                const incorrectValue = data.datasets[1].data[index] || 0;
                const total = correctValue + incorrectValue;

                return `Total Questions: ${total}`;
              },
            },
            titleColor: darkMode
              ? tooltipTitleColor.dark
              : tooltipTitleColor.light,
            footerColor: darkMode
              ? tooltipTitleColor.dark
              : tooltipTitleColor.light,
            bodyColor: darkMode
              ? tooltipBodyColor.dark
              : tooltipBodyColor.light,
            backgroundColor: darkMode
              ? tooltipBgColor.dark
              : tooltipBgColor.light,
            borderColor: darkMode
              ? tooltipBorderColor.dark
              : tooltipBorderColor.light,
          },
          legend: {
            display: false,
          },
        },
        interaction: {
          intersect: false,
          mode: "nearest",
        },
        maintainAspectRatio: false,
        resizeDelay: 200,
      },
    });
    setChart(newChart);
    return () => newChart.destroy();
  }, [data, useTimeScale]);

  useEffect(() => {
    if (!chart) return;

    if (darkMode) {
      chart.options.plugins.tooltip.bodyColor = tooltipBodyColor.dark;
      chart.options.plugins.tooltip.backgroundColor = tooltipBgColor.dark;
      chart.options.plugins.tooltip.borderColor = tooltipBorderColor.dark;
    } else {
      chart.options.plugins.tooltip.bodyColor = tooltipBodyColor.light;
      chart.options.plugins.tooltip.backgroundColor = tooltipBgColor.light;
      chart.options.plugins.tooltip.borderColor = tooltipBorderColor.light;
    }
    chart.update("none");
  }, [currentTheme]);

  return <canvas ref={canvas} width={width} height={height}></canvas>;
}

export default LineChartAnswers;
